import Plugin from "@ckeditor/ckeditor5-core/src/plugin";
import { toWidget } from "@ckeditor/ckeditor5-widget/src/utils";
import Widget from "@ckeditor/ckeditor5-widget/src/widget";
import Command from "@ckeditor/ckeditor5-core/src/command";
import EmitterMixin from "@ckeditor/ckeditor5-utils/src/emittermixin";
import mix from "@ckeditor/ckeditor5-utils/src/mix";

class TrainingLink extends Plugin {
  static get requires() {
    return [TrainingLinkEditing];
  }
}

class TrainingLinkEditing extends Plugin {
  static get requires() {
    return [Widget];
  }

  init() {
    if (this.editor.config.get("trainingLinks")?.trainingLinkRenderer) {
      this._defineSchema();
      this._defineConverters();

      this.editor.commands.add(
        "insertTrainingLink",
        new InsertTrainingLinkCommand(this.editor)
      );

      this.editor.on("addTrainingLink", (eventInfo, training) => {
        this.editor.execute("insertTrainingLink", {
          training,
        });
      });
    }
  }

  _defineSchema() {
    const schema = this.editor.model.schema;

    schema.register("trainingLink", {
      // Behaves like a self-contained object (e.g. an image).
      isObject: true,

      // Allow in places where other blocks are allowed (e.g. directly in the root).
      allowWhere: "$block",
      allowAttributes: [
        "id",
        "content-repository-attachable-id",
        "course-number",
        "course-type",
        "flags",
        "image-url",
        "manual-name",
        "name",
        "training-object-id",
      ],
    });
  }

  _defineConverters() {
    const editor = this.editor;
    const conversion = editor.conversion;
    const renderTrainingLink =
      editor.config.get("trainingLinks").trainingLinkRenderer;

    conversion.for("upcast").add((dispatcher) => {
      dispatcher.on("element:section", (evt, data, conversionApi) => {
        // Prevent copying and pasting training links
        if (
          data.viewItem.getAttribute("data-widget-type") === "training" &&
          data.modelCursor?.root?.name == "$clipboardHolder"
        ) {
          const attachableId = data.viewItem.getAttribute(
            "data-content-repository-attachable-id"
          );
          const element = document.querySelector(
            `[data-content-repository-attachable-id="${attachableId}"]`
          );
          if (element && !element.classList.contains("ck-widget_selected")) {
            // Checks to make sure the training link is actually being pasted
            // rather than being moved within the document
            evt.stop();
            return;
          }
        }
      });
    });

    conversion.for("upcast").elementToElement({
      view: {
        name: "section",
        classes: "training-link",
      },
      model: (viewElement, { writer: modelWriter }) => {
        // Read the "data-id" attribute from the view and set it as the "id" in the model.
        return modelWriter.createElement("trainingLink", {
          id: parseInt(viewElement.getAttribute("data-id")),
          name: viewElement.getAttribute("data-name"),
          "content-repository-attachable-id": viewElement.getAttribute(
            "data-content-repository-attachable-id"
          ),
          "course-number": viewElement.getAttribute("data-course-number"),
          "course-type": viewElement.getAttribute("data-course-type"),
          "image-url": viewElement.getAttribute("data-image-url"),
          flags: viewElement.getAttribute("data-flags"),
          "manual-name": viewElement.getAttribute("data-manual-name"),
          "training-object-id": viewElement.getAttribute(
            "data-training-object-id"
          ),
        });
      },
    });

    conversion.for("dataDowncast").elementToElement({
      model: "trainingLink",
      view: (modelElement, { writer: viewWriter }) => {
        return viewWriter.createEmptyElement("section", {
          class: "training-link",
          "data-id": modelElement.getAttribute("id"),
          "data-name": modelElement.getAttribute("name"),
          "data-content-repository-attachable-id": modelElement.getAttribute(
            "content-repository-attachable-id"
          ),
          "data-course-number": modelElement.getAttribute("course-number"),
          "data-course-type": modelElement.getAttribute("course-type"),
          "data-image-url": modelElement.getAttribute("image-url"),
          "data-flags": modelElement.getAttribute("flags"),
          "data-manual-name": modelElement.getAttribute("manual-name"),
          "data-training-object-id":
            modelElement.getAttribute("training-object-id"),
          "data-widget-type": "training",
        });
      },
    });

    conversion.for("editingDowncast").elementToElement({
      model: "trainingLink",
      view: (modelElement, { writer: viewWriter }) => {
        const id = modelElement.getAttribute("id");
        const name = modelElement.getAttribute("name");
        const contentRepositoryAttachableId = modelElement.getAttribute(
          "content-repository-attachable-id"
        );
        const courseNumber = modelElement.getAttribute("course-number");
        const courseType = modelElement.getAttribute("course-type");
        const imageUrl = modelElement.getAttribute("image-url");
        const flags = modelElement.getAttribute("flags");
        const manualName = modelElement.getAttribute("manual-name");
        const trainingObjectId =
          modelElement.getAttribute("training-object-id");

        const section = viewWriter.createContainerElement("section", {
          class: "training-link",
          "data-id": id,
          "data-name": name,
          "data-content-repository-attachable-id":
            contentRepositoryAttachableId,
          "data-course-number": courseNumber,
          "data-course-type": courseType,
          "data-image-url": imageUrl,
          "data-flags": flags,
          "data-manual-name": manualName,
          "data-training-object-id": trainingObjectId,
          "data-widget-type": "training",
        });

        const reactWrapper = viewWriter.createRawElement(
          "div",
          {
            class: "training-link__react-wrapper",
          },
          function (domElement) {
            renderTrainingLink(
              {
                id,
                name,
                contentRepositoryAttachableId,
                courseNumber,
                courseType,
                imageUrl,
                flags,
                manualName,
                trainingObjectId,
              },
              domElement
            );
          }
        );

        viewWriter.insert(
          viewWriter.createPositionAt(section, 0),
          reactWrapper
        );

        return toWidget(section, viewWriter, {
          label: "training link widget",
        });
      },
    });
  }
}

class InsertTrainingLinkCommand extends Command {
  execute({ training }) {
    this.editor.model.change((writer) => {
      const trainingLink = writer.createElement("trainingLink", {
        id: training.id,
        name: training.name,
        "content-repository-attachable-id":
          training.contentRepositoryAttachableId,
        "course-number": training.courseNumber,
        "course-type": training.courseType,
        "image-url": training.imageUrl,
        flags: training.flags,
        "manual-name": training.manualName,
        "training-object-id": training.trainingObjectId,
      });
      this.editor.model.insertContent(trainingLink);
      this.editor.execute("insertParagraph", {
        position: this.editor.model.createPositionAfter(trainingLink),
      });
    });
  }

  refresh() {
    const model = this.editor.model;
    const selection = model.document.selection;
    const allowedIn = model.schema.findAllowedParent(
      selection.getFirstPosition(),
      "trainingLink"
    );

    this.isEnabled = allowedIn !== null;
  }
}

mix(TrainingLink, EmitterMixin);

export default TrainingLink;
